import { Box, Button, DialogActions, DialogContent, Typography } from "@mui/material";
import { StaffAgreement } from "../types";
import { useStore } from "../../../store/store";
import { useState } from "react";
import { useField } from "../../../api/useField";
import { FormFields } from "../../../components/entityForm/EntityForm";
import { useForm, UseFormReturn } from "react-hook-form";
import { AppDialog } from "../../../store/store.types";
import { FileField } from "../../../components/fieldsViews/fields/FileField";
import { DateInputField } from "../../../components/fieldsInputs/fields/DateInputField";
import { FormatedField } from "../../../types/fields.types";
import { Close as CloseIcon } from "@mui/icons-material";

type StaffAgreementFilesProps = {
	staffAgreementId: string;
	selectedMonth: number;
	selectedYear: number;
	files: StaffAgreement["files"];
};

const STAFF_AGREEMENT_FILES = "staff_agreements_files_1";

export const StaffAgreementFiles = ({
	staffAgreementId,
	selectedMonth,
	selectedYear,
	files,
}: StaffAgreementFilesProps) => {
	const { appDialog } = useStore();

	const { formatedField: filesField } = useField("staff_agreements", "files");
	const { formatedField: uploadDateField } = useField(STAFF_AGREEMENT_FILES, "upload_date");

	const entityForm = useForm<FormFields>({
		defaultValues: {
			// Using 13:00 to avoid timezone/DST issues that could shift the day if using midnight
			upload_date: new Date(selectedYear, selectedMonth, 1, 13, 0, 0),
		},
	});

	const staffPerMonthFiles: StaffAgreement["files"] = files.filter((item) => {
		const uploadDate = new Date(item.upload_date);
		const fileMonth = uploadDate.getMonth();
		const fileYear = uploadDate.getFullYear();
		return fileMonth === selectedMonth && fileYear === selectedYear;
	});
	if (!filesField || !uploadDateField) return null;

	const handleOpenAddFileDialog = () => {
		entityForm.reset();
		appDialog.createDialog({
			title: "Adicionar Ficheiro",
			contentAndActions: (
				<StaffAddFileDialog
					entityForm={entityForm}
					uploadDateField={uploadDateField}
					filesField={filesField}
					staffAgreementId={staffAgreementId}
					appDialog={appDialog}
					selectedMonth={selectedMonth}
					selectedYear={selectedYear}
				/>
			),
			allowSoftCancel: false,
		});
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
			<FileField
				value={staffPerMonthFiles}
				field={filesField}
				fieldView="group"
				entity="staff_agreements"
				id={staffAgreementId}
				extraOptions={{
					fileFolder: "c0a8199b-be41-4588-93a8-d572329f6418",
					field: "files",
					disabled: true,
					hidden: {
						create: true,
						update: true,
					},
				}}
			/>

			<Button onClick={handleOpenAddFileDialog} sx={{ mt: staffPerMonthFiles.length > 0 ? 2 : 0 }}>
				Associar Ficheiro
			</Button>
		</Box>
	);
};

type StaffAddFileDialogProps = {
	entityForm: UseFormReturn<FormFields>;
	uploadDateField: FormatedField;
	filesField: FormatedField;
	staffAgreementId: string;
	appDialog: AppDialog;
	selectedMonth: number;
	selectedYear: number;
};
const StaffAddFileDialog = ({
	entityForm,
	uploadDateField,
	filesField,
	staffAgreementId,
	appDialog,
	selectedMonth,
	selectedYear,
}: StaffAddFileDialogProps) => {
	const uploadDate = entityForm.watch("upload_date");
	const minDate = new Date(selectedYear, selectedMonth, 1);
	const maxDate = new Date(selectedYear, selectedMonth + 1, 0);
	const [isUploading, setIsUploading] = useState(false);

	const handleCancel = () => {
		appDialog.toggleIsOpen();
	};

	return (
		<>
			<DialogContent>
				<Box sx={{ mb: 4 }}>
					<Typography variant="body1" sx={{ mb: 2 }}>
						Selecione a data do ficheiro:
					</Typography>
					<DateInputField
						field={uploadDateField}
						value={uploadDate}
						entityForm={entityForm}
						options={{ minDate, maxDate }}
					/>
				</Box>
				<FileField
					value={[]}
					field={filesField}
					fieldView="group"
					entity="staff_agreements"
					id={staffAgreementId}
					extraOptions={{
						fileFolder: "c0a8199b-be41-4588-93a8-d572329f6418",
						field: "files",
						disabled: !uploadDate,
						hidden: {
							update: true,
						},
						additionalData: {
							upload_date: uploadDate?.toISOString(),
						},
						onUploadStart: () => {
							setIsUploading(true);
						},
						onUploadEnd: () => {
							setIsUploading(false);
							appDialog.toggleIsOpen();
						},
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Button disabled={isUploading} onClick={handleCancel} color="secondary" startIcon={<CloseIcon />}>
					Cancelar
				</Button>
			</DialogActions>
		</>
	);
};
